/**
 * FormNode - Unified workflow node with Tiptap content
 *
 * Implements:
 * - FORM-01: Form Node encapsulates Tiptap content + JSON Schema
 * - FORM-02: Exposes typed output ports (data, attachments)
 * - FORM-05: Media fields generate attachments metadata
 *
 * Design: Form Nodes are visually similar to input/output nodes but contain
 * a rich Tiptap editor for defining form fields. The content is compiled
 * to JSON Schema at publish time.
 */

import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

/**
 * Form Node data structure
 */
export interface FormNodeData {
  /** Node label */
  label: string;
  /** Tiptap JSON content */
  content: Record<string, unknown>;
  /** Whether node is in edit mode */
  isEditing?: boolean;
  /** Compiled field count (for display) */
  fieldCount?: number;
  /** Validation status */
  validationStatus?: 'valid' | 'warning' | 'error';
  /** Validation message */
  validationMessage?: string;
  /** Index signature for React Flow */
  [key: string]: unknown;
}

/**
 * FormNodeType - React Flow node type for form nodes
 *
 * Use this when you need type safety for form nodes:
 * - In node arrays: Node<FormNodeData, 'form'>[]
 * - In onNodesChange handlers
 */
export type FormNodeType = Node<FormNodeData, 'form'>;

/**
 * FormNode Component
 *
 * A workflow node that contains a Tiptap editor for defining form fields.
 * The user writes form content (labels, fields, help text) and the system
 * compiles it to JSON Schema at publish time.
 */
export const FormNode = memo(({ data, selected }: NodeProps<Node<FormNodeData>>) => {
  const [isEditing, setIsEditing] = useState(data?.isEditing ?? false);

  // Initialize Tiptap editor for form content
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable history extension - handled by Yjs
        // Note: In Tiptap 3.x, the history option was renamed to 'undoRedo'
      }),
      Placeholder.configure({
        placeholder: 'Define your form fields here...\n\nExample:\nName: [text, required]\nEmail: [email, required]\nMessage: [textarea]',
      }),
    ],
    content: data?.content || '',
    editable: isEditing,
    onUpdate: ({ editor }) => {
      // Content updates are synced via Yjs in BoardCanvas
    },
  });

  // Toggle edit mode
  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
    editor?.setEditable(true);
  }, [editor]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    editor?.setEditable(false);
  }, [editor]);

  // Determine validation badge
  const validationBadge = data?.validationStatus === 'error' ? (
    <span className="validation-badge error" title={data?.validationMessage}>!</span>
  ) : data?.validationStatus === 'warning' ? (
    <span className="validation-badge warning" title={data?.validationMessage}>!</span>
  ) : null;

  return (
    <>
      {/* Allow resizing when selected */}
      <NodeResizer
        minWidth={200}
        minHeight={100}
        isVisible={selected ?? false}
        lineClassName="resize-line"
        handleClassName="resize-handle"
      />

      <div
        className={`form-node ${selected ? 'selected' : ''} ${isEditing ? 'editing' : ''}`}
        onDoubleClick={handleDoubleClick}
      >
        {/* Header */}
        <div className="form-node-header">
          <span className="form-node-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <line x1="7" y1="9" x2="17" y2="9" />
              <line x1="7" y1="13" x2="13" y2="13" />
            </svg>
          </span>
          <span className="form-node-label">{data?.label || 'Form'}</span>
          {validationBadge}
          {data?.fieldCount !== undefined && (
            <span className="field-count">{data.fieldCount} fields</span>
          )}
        </div>

        {/* Tiptap Content */}
        <div className="form-node-content" onBlur={handleBlur}>
          <EditorContent editor={editor} />
        </div>

        {/* Output ports (FORM-02) */}
        <div className="form-node-ports">
          <div className="port-row">
            <span className="port-label">data</span>
            <Handle
              type="source"
              position={Position.Right}
              id="data"
              className="port-handle data"
            />
          </div>
          <div className="port-row">
            <span className="port-label">attachments</span>
            <Handle
              type="source"
              position={Position.Right}
              id="attachments"
              className="port-handle attachments"
            />
          </div>
        </div>
      </div>

      <style>{`
        .form-node {
          background: var(--color-surface);
          border: 2px solid var(--color-border);
          border-radius: 8px;
          min-width: 200px;
          min-height: 100px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .form-node.selected {
          border-color: var(--color-accent);
          box-shadow: 0 0 0 2px var(--color-focus-ring);
        }

        .form-node.editing {
          border-color: var(--color-primary);
        }

        .form-node-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--color-border);
          background: var(--color-surface-muted);
          border-radius: 6px 6px 0 0;
        }

        .form-node-icon {
          display: flex;
          color: var(--color-accent);
        }

        .form-node-icon svg {
          width: 16px;
          height: 16px;
        }

        .form-node-label {
          font-weight: 600;
          font-size: 13px;
          color: var(--color-text);
        }

        .validation-badge {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          color: white;
        }

        .validation-badge.error {
          background: var(--color-error);
        }

        .validation-badge.warning {
          background: var(--color-warning);
        }

        .field-count {
          margin-left: auto;
          font-size: 11px;
          color: var(--color-text-muted);
        }

        .form-node-content {
          padding: 12px;
          min-height: 60px;
        }

        .form-node-content .tiptap {
          outline: none;
          font-size: 13px;
          line-height: 1.5;
        }

        .form-node-content .tiptap p.is-editor-empty:first-child::before {
          color: var(--color-text-muted);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
          white-space: pre-wrap;
        }

        .form-node-ports {
          padding: 8px 12px;
          border-top: 1px solid var(--color-border);
          background: var(--color-surface-muted);
          border-radius: 0 0 6px 6px;
        }

        .port-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          margin-bottom: 4px;
        }

        .port-row:last-child {
          margin-bottom: 0;
        }

        .port-label {
          font-size: 11px;
          color: var(--color-text-muted);
        }

        .port-handle {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid;
          background: var(--color-surface);
        }

        .port-handle.data {
          border-color: var(--color-primary);
        }

        .port-handle.attachments {
          border-color: var(--color-accent);
        }

        .resize-line {
          border-color: var(--color-accent) !important;
        }

        .resize-handle {
          width: 8px !important;
          height: 8px !important;
          border-radius: 2px !important;
          background: var(--color-accent) !important;
          border: none !important;
        }
      `}</style>
    </>
  );
});

FormNode.displayName = 'FormNode';
